import { z } from 'zod';

// SparkGate: generic feature gating. The backend evaluates per-feature targeting
// rules against the user's context and returns a show/hide flag per feature.

export const SPARKGATE_PLATFORMS = ['ios', 'android', 'web'] as const;
export type SparkGatePlatform = typeof SPARKGATE_PLATFORMS[number];

export const SPARKGATE_FEATURE_KEYS = ['app_banner'] as const;
export type SparkGateFeatureKey = typeof SPARKGATE_FEATURE_KEYS[number];

// A flat set of conditions checked against one user. Every field is optional;
// omitted/empty = no restriction on that dimension. Within a dimension the values
// are OR-ed (match any one); across dimensions they are AND-ed (match all present).
// `.strict()`: unknown keys are rejected so typos/misuse fail loudly at write time.
export const sparkGateConditionsSchema = z
  .object({
    markets: z.array(z.string()).optional(),
    platforms: z.array(z.enum(SPARKGATE_PLATFORMS)).optional(),
    user_ids: z.array(z.string()).optional(),
    account_ids: z.array(z.string()).optional(),
    location_ids: z.array(z.string()).optional(),
    exclude_native_app: z.boolean().optional(),
    min_app_version: z.number().nullable().optional(),
    max_app_version: z.number().nullable().optional(),
  })
  .strict();
export type SparkGateConditions = z.infer<typeof sparkGateConditionsSchema>;

// One OR-group: positive conditions plus an optional `not` exclusion. A group
// matches when its conditions match AND its `not` block does NOT match. Groups are
// one level deep — they may carry a `not`, but a nested `any` is rejected (strict).
export const sparkGateGroupSchema = sparkGateConditionsSchema
  .extend({
    not: sparkGateConditionsSchema.optional(),
  })
  .strict();
export type SparkGateGroup = z.infer<typeof sparkGateGroupSchema>;

// Targeting combines three parts (see docs/features/sparkgate-targeting-rules.md):
//   - base conditions (the flat fields) that ALWAYS apply, AND-ed into every branch
//   - `any`: OR-groups; when present the user must match at least one group
//   - `not`: a base-level exclusion; matching users are hidden regardless of `any`
// The legacy flat shape (just the base fields) keeps working unchanged.
export const sparkGateTargetingSchema = sparkGateConditionsSchema
  .extend({
    any: z.array(sparkGateGroupSchema).optional(),
    not: sparkGateConditionsSchema.optional(),
  })
  .strict();
export type SparkGateTargeting = z.infer<typeof sparkGateTargetingSchema>;

export const sparkGateFeatureSchema = z
  .object({
    enabled: z.boolean(),
    targeting: sparkGateTargetingSchema.optional(),
  })
  .strict();
export type SparkGateFeature = z.infer<typeof sparkGateFeatureSchema>;

export const sparkGateFeaturesSchema = z.record(sparkGateFeatureSchema);
export type SparkGateFeatures = z.infer<typeof sparkGateFeaturesSchema>;

export interface SparkGateUserContext {
  userId: string;
  accountId?: string;
  markets: string[];
  locationIds: string[];
  platform: SparkGatePlatform;
  isNativeApp: boolean;
  appVersion?: number;
}

// Feature key -> enabled for the current user.
export type SparkGateEvaluation = Record<string, boolean>;

export interface GetSparkGateResponseBody {
  features: SparkGateEvaluation;
}

export interface PublicSparkGateConfig {
  version: number;
  updatedAt?: string;
  updatedBy?: string;
  features: SparkGateFeatures;
}

// Admin write endpoint contract. `expectedVersion` enables optimistic concurrency:
// the write is rejected if it doesn't match the stored version.
export interface UpdateSparkGateRequestBody {
  features: SparkGateFeatures;
  expectedVersion?: number;
}

// Re-export the shared helpers (validation, plain-English summary, diff) so both
// the API and the Control Center UI evaluate rules from one source of truth.
export * from './validation';
export * from './describe';
export * from './diff';
